import { pipe, path, prop, contains, hasPath } from 'ramda'
import tryParseJson from './tryParseJson.js'

export const extractResponseData = path(['response', 'data'])

/**
 * @readonly
 * @enum {string}
 */
export const ExceptionOrigin = Object.freeze({
  DOTNET: 'dotnet',
  LARAVEL: 'laravel',
  AXIOS: 'axios',
  JAVASCRIPT: 'javascript',
})

export class ParsedException {
  /**
   * @param {string} origin
   * @param {Object} data
   * @param {number} status
   */
  constructor(origin, data, status) {
    this.origin = origin
    this.data = data
    this.status = status
  }
}

export class DotnetValidationError {
  /**
   * @param {Object} raw
   * @param {string} raw.FriendlyDescription
   * @param {string} raw.TechnicalDescription
   * @param {string|Object} raw.AdditionalInformation
   */
  constructor(raw) {
    this.code = raw.code
    this.FriendlyDescription = raw.FriendlyDescription
    this.TechnicalDescription = raw.TechnicalDescription
    this.AdditionalInformation = raw.AdditionalInformation
  }
}

export class DotnetException {
  /**
   * @param {string} raw
   */
  constructor(raw) {
    this.message = raw
    this.trace = raw.split('\r\n')
  }
}

export class LaravelException {
  /**
   * @param {Object} raw
   * @param {string} raw.message
   * @param {string} raw.exception
   * @param {string} raw.file
   * @param {number} raw.line
   * @param {Object[]} raw.trace
   */
  constructor(raw) {
    this.message = raw.message
    this.exception = raw.exception
    this.file = raw.file
    this.line = raw.line
    this.trace = raw.trace
  }
}

const toParsedException = status => origin => x => new ParsedException(origin, x, status)

/**
 * @param {Exception} e
 * @returns {ParsedException}
 */
export function parseAxiosException(e) {
  // first get message on error
  const laravelMessage = path(['response', 'data', 'message'])(e)
  const messageExists = hasPath(['response', 'data', 'message'])(e)
  const parseAs = toParsedException(e?.response?.status || 0)

  // if the message is a json it's assumed to be a validation error from the dotnet API
  const dotnetObject = tryParseJson(laravelMessage)
  if (dotnetObject) {
    const mapDotnetObject =
      typeof dotnetObject === 'object' ? x => new DotnetValidationError(x) : x => x
    return pipe(mapDotnetObject, parseAs(ExceptionOrigin.DOTNET))(dotnetObject)
  }
  // if the message contains '.cs:line' assume it's a dotnet exception
  if (laravelMessage && contains('.cs:line')(laravelMessage)) {
    return pipe(x => new DotnetException(x), parseAs(ExceptionOrigin.DOTNET))(laravelMessage)
  }

  const laravelParser = pipe(
    path(['response', 'data']),
    x => new LaravelException(x),
    parseAs(ExceptionOrigin.LARAVEL),
  )
  const axiosParser = pipe(prop('response'), parseAs(ExceptionOrigin.AXIOS))
  const jsParser = parseAs(ExceptionOrigin.JAVASCRIPT)

  // if it's not a json, but it does contain response.data.message, it's assumed to be a laravel exception
  const parser = laravelMessage
    ? laravelParser
    : path(['response', 'status'], e) // if it contains response.status, we assume the exception is caused by axios
    ? axiosParser
    : jsParser // any other exception is assumed to be caused by javascript

  return parser(e)
}
